import { Button, Col, Row, Spinner, Modal, Form } from "react-bootstrap";
import { SuperTrackMarkupSuperTrack } from "./SuperTrackMarkupSuperTrack";
import { SuperTrackMarkupControls } from "./SuperTrackMarkupControls";
import { SuperTrackMarkupPlayerSelect } from "@/pages/SuperTracks/SuperTrackMarkupPlayerSelect";
import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import {
  addSuperTrack,
  setSuperTracks,
  setSelectedTracks,
  setSelectedSuperTrack,
  setSelectedSuperTracks,
  updateSelectedTracks,
  updateMatch,
} from "@/store/supertrackSlice";
import { matchApi } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { cloneDeep } from "lodash";
import { TEAM_COLORS } from "@/constants/teams";
import CustomSwitch from "@/components/Elements/switch";
import { useSuperTracks } from "@/hooks/useSuperTracks";
import * as Sentry from "@sentry/react";
import { Profiler } from "react";

const MemoizedSuperTrackControls = React.memo(SuperTrackMarkupControls);
const MemoizedSuperTrack = React.memo(SuperTrackMarkupSuperTrack);
const MemoizedSuperTrackPlayerSelect = React.memo(SuperTrackMarkupPlayerSelect);

const SuperTrackMarkupPanel = forwardRef(function SuperTrackMarkupPanel(
  props,
  posRef
) {
  const { videoId } = useParams();
  const dispatch = useDispatch();
  const { hasIntersection } = useSuperTracks();
  const { superTracks, selectedSuperTracks } = useSelector(
    ({ superTracks }) => superTracks
  );
  const { user } = useSelector(({ auth }) => auth); // Add this line to get user info
  const { match } = useSelector(({ superTracks }) => superTracks);
  const [localSuperTracks, setLocalSuperTracks] = useState([]);
  const [allSuperTracks, setAllSuperTracks] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [reloading, setReloading] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [showAllTracks, setShowAllTracks] = useState(false);
  const [editTeams, setEditTeams] = useState({
    homeTeam: { color: "" },
    guestTeam: { color: "" },
  });
  const [jerseys, setJerseys] = useState({});

  // Ref to store start time
  const renderStartRef = useRef();

  // Start timing before rendering tracks
  renderStartRef.current = performance.now();

  const onRenderCallback = (
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    endTime,
    interactions
  ) => {
    console.log(
      `Profiler ${id} (${phase}): actualDuration = ${actualDuration}ms, baseDuration = ${baseDuration}ms`
    );
    if (actualDuration > 100) {
      Sentry.captureException(
        new Error(`Profiler ${id} took ${actualDuration} ms to render`),
        {
          tags: {
            error_type: "render_performance",
            page: "SuperTrackMarkupPanel",
            phase,
          },
          extra: {
            actualDuration,
            baseDuration,
            startTime,
            endTime,
            trackCount: allSuperTracks.length,
          },
        }
      );
    }
  };

  // Function to calculate if a superTrack has duplicate jerseys with intersections
  const calculateIsDuplicate = useCallback(
    (superTrack) => {
      if (!superTrack.jersey || !superTrack.kind) return false;

      const teamJerseys = jerseys?.[superTrack.kind]?.[superTrack.jersey];
      if (!teamJerseys || teamJerseys.length < 2) return false;

      // Check if current superTrack has intersection with other tracks having the same jersey
      const otherTrackIds = teamJerseys.filter((id) => id !== superTrack.id);
      return otherTrackIds.some((otherId) =>
        hasIntersection({ targetId: superTrack.id, superTrackId: otherId })
      );
    },
    [jerseys, hasIntersection]
  );

  const fetchTaskStatuses = (videoId) => {
    setReloading(true);
    matchApi
      .getTaskStatuses(videoId)
      .then((res) => {
        setTaskStatuses(res);
        setReloading(false);
      })
      .catch((err) => {
        toast.warning("Something went wrong.");
        setReloading(false);
      });
  };

  useEffect(() => {
    fetchTaskStatuses(videoId);
  }, [videoId]);

  useEffect(() => {
    if (!taskStatuses) return;

    const visibleSuperTracks = localSuperTracks.filter(
      ({ isHidden }) => !isHidden
    );
    const hiddenSuperTracks = localSuperTracks.filter(
      ({ isHidden }) => isHidden
    );

    let tracksToShow = [...visibleSuperTracks, ...hiddenSuperTracks];

    // If not showing all tracks, only show tracks that have task statuses
    const taskTracks = tracksToShow.filter((track) =>
      Object.keys(taskStatuses).includes(track.id)
    );

    if (!showAllTracks) {
      setAllSuperTracks(taskTracks);
    } else {
      setAllSuperTracks(tracksToShow);
    }

    const jerseys = {
      left_team: {},
      right_team: {},
    };
    for (const track of taskTracks) {
      if (
        track.jersey &&
        (track.kind === "left_team" || track.kind === "right_team")
      ) {
        const team = track.kind;
        jerseys[team][track.jersey] = jerseys[team][track.jersey]
          ? [...jerseys[team][track.jersey], track.id]
          : [track.id];
      }
    }

    setJerseys(jerseys);
  }, [localSuperTracks, showAllTracks, taskStatuses]);

  useEffect(() => {
    if (!superTracks) return;
    const tracks = Object.entries(superTracks).map(([id, data]) => ({
      ...data,
      id,
    }));
    setLocalSuperTracks(tracks);
  }, [superTracks]);

  // Add this useEffect to update editTeams when match changes
  useEffect(() => {
    const leftColor = match?.left_team_jersey_color || match?.match_colors?.[0];
    const rightColor =
      match?.right_team_jersey_color || match?.match_colors?.[1];

    if (leftColor || rightColor) {
      setEditTeams({
        homeTeam: { color: leftColor || "" },
        guestTeam: { color: rightColor || "" },
      });
    }
  }, [match]);

  // TODO: we should split hide and select logic but now it's in one place (selectedTracks)
  const onSuperTrackHide = useCallback(
    (superTrackId) => {
      const clone = [...localSuperTracks];
      const index = clone.findIndex(({ id }) => id === superTrackId);

      clone[index].isHidden = !clone[index].isHidden;

      const newSuperTracks = {
        ...superTracks,
        [superTrackId]: {
          ...superTracks[superTrackId],
          isHidden: !superTracks[superTrackId].isHidden,
        },
      };
      dispatch(setSuperTracks(newSuperTracks));

      // Only update selectedTracks if it's the selected super track.
      // This flow is not a good choice :(
      if (selectedSuperTracks[superTrackId]) {
        dispatch(
          updateSelectedTracks({
            isAdding: !newSuperTracks[superTrackId].isHidden,
            ids: superTracks[superTrackId].tracks.map(
              ({ track_id }) => track_id
            ),
          })
        );
      }
    },
    [localSuperTracks, superTracks]
  );

  const selectAll = () => {
    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    let selectedTrackIds = [];
    for (let id in superTracks) {
      clonedSelectedSuperTracks[id] = true;
      selectedTrackIds = [
        ...selectedTrackIds,
        ...superTracks[id].tracks.map(({ track_id }) => track_id),
      ];
    }
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));
    dispatch(setSelectedTracks(selectedTrackIds));
  };

  const deselectAll = () => {
    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    for (let id in clonedSelectedSuperTracks) {
      clonedSelectedSuperTracks[id] = false;
    }
    dispatch(setSelectedTracks([]));
    dispatch(setSelectedSuperTrack(null));
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));
  };

  const createSuperTrack = async () => {
    try {
      const superTrack = await matchApi.createSuperTrack(videoId);
      dispatch(addSuperTrack(superTrack));
    } catch (error) {
      console.error(error);
    }
  };

  const handleColorEdit = async () => {
    try {
      await matchApi.updateTeamColors(videoId, {
        leftTeamColor: editTeams.homeTeam.color,
        rightTeamColor: editTeams.guestTeam.color,
      });

      // Fetch updated match data
      const updatedMatch = await matchApi.getMatch(videoId);
      if (!updatedMatch) return;
      dispatch(updateMatch(updatedMatch));

      setShowColorModal(false);
      toast.success("Colors updated successfully");
    } catch (err) {
      console.log(err);
      toast.error("Failed to update colors");
    }
  };

  // Update the modal open handler
  const openColorModal = () => {
    const leftColor = match?.left_team_jersey_color || match?.match_colors?.[0];
    const rightColor =
      match?.right_team_jersey_color || match?.match_colors?.[1];

    setEditTeams({
      homeTeam: { color: leftColor || "" },
      guestTeam: { color: rightColor || "" },
    });
    setShowColorModal(true);
  };

  const renderColorOptions = useCallback(() => {
    if (!match?.match_colors?.length) {
      return <option disabled>No colors available</option>;
    }

    return TEAM_COLORS.filter((color) =>
      match.match_colors.includes(color.value)
    ).map((color) => (
      <option key={color.value} value={color.value}>
        {color.label}
      </option>
    ));
  }, [match?.match_colors]);

  // Toggle handler for Show All
  const toggleShowAllTracks = () => {
    setShowAllTracks((prev) => !prev);
  };

  useEffect(() => {
    // This effect runs after the DOM has been updated (painted)
    const renderEnd = performance.now();
    const renderDuration = renderEnd - renderStartRef.current;
    console.log(
      `Rendering all tracks took ${renderDuration.toFixed(2)} ms`,
      renderDuration
    );
    if (renderDuration > 10000) {
      Sentry.captureException(
        new Error(`Rendering all tracks took ${renderDuration.toFixed(2)} ms`),
        {
          tags: {
            error_type: "render_performance",
            page: "SuperTrackMarkupPanel",
          },
          extra: {
            renderDuration: renderDuration,
            timestamp: new Date().toISOString(),
            trackCount: allSuperTracks.length,
          },
        }
      );
    }
  }, [allSuperTracks]);

  return (
    <>
      <Row className="justify-content-center mt-0">
        <Col
          xs={11}
          className="p-0 d-flex justify-content-between align-items-center"
        >
          <div>
            <Button
              variant="success"
              size="sm"
              className="mx-1 my-1"
              onClick={selectAll}
            >
              Select All
            </Button>
            <Button
              variant="success"
              size="sm"
              className="mx-1 my-1"
              onClick={deselectAll}
            >
              Deselect All
            </Button>
            <Button
              variant="success"
              size="sm"
              className="mx-1 my-1"
              onClick={createSuperTrack}
            >
              Add Super Track
            </Button>
            <CustomSwitch
              label="Show All:"
              checked={showAllTracks}
              reverse={true}
              onChange={setShowAllTracks}
            />
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-success"
              size="sm"
              className="mx-1 my-1"
              onClick={openColorModal}
            >
              Edit Color
            </Button>
            <Button
              variant="success"
              size="sm"
              className="mx-1 my-1 d-flex gap-1 align-items-center"
              onClick={() => fetchTaskStatuses(videoId)}
            >
              Reload
              {reloading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <FontAwesomeIcon
                  className="text-grey"
                  icon="fa-solid fa-arrows-rotate"
                />
              )}
            </Button>
            <span className="d-flex gap-1 align-items-center">
              <span>To Do</span>
              <FontAwesomeIcon
                className="text-grey"
                icon="fa-solid fa-circle"
              />
            </span>
            <span className="d-flex gap-1 align-items-center">
              <span>In Progress</span>
              <FontAwesomeIcon
                className="text-primary"
                icon="fa-solid fa-circle"
              />
            </span>
            <span className="d-flex gap-1 align-items-center">
              <span>Done</span>
              <FontAwesomeIcon
                className="text-success"
                icon="fa-solid fa-circle"
              />
            </span>
          </div>
        </Col>
      </Row>
      <Row className="justify-content-center mt-1">
        <Col xs={11} className="super-track__container p-0">
          <ScrollSync>
            <Profiler id="SuperTracks" onRender={onRenderCallback}>
              <div className="super-track__panel">
                <div className="super-track__track-controls-outer">
                  <ScrollSyncPane>
                    <div className="super-track__track-controls">
                      {allSuperTracks.map((superTrack) => (
                        <MemoizedSuperTrackControls
                          key={`${superTrack.id}- ${
                            superTrack.isHidden ? "hidden" : "visible"
                          }`}
                          superTrack={superTrack}
                          onHide={onSuperTrackHide}
                          task={taskStatuses[superTrack.id]}
                          isManager={user?.is_staff} // Add this prop
                        />
                      ))}
                    </div>
                  </ScrollSyncPane>
                </div>
                <div className="super-track__panel-track-container">
                  <div className="super-track__indicator" ref={posRef} />
                  <ScrollSyncPane>
                    <div className="inner-container">
                      {allSuperTracks.map((superTrack) => (
                        <MemoizedSuperTrack
                          key={`${superTrack.id}- ${
                            superTrack.isHidden ? "hidden" : "visible"
                          }`}
                          superTrack={superTrack}
                          isDuplicate={calculateIsDuplicate(superTrack)}
                        />
                      ))}
                    </div>
                  </ScrollSyncPane>
                </div>
                <div className="super-track__player-select-outer">
                  <ScrollSyncPane>
                    <div className="super-track__player-select">
                      {allSuperTracks.map((superTrack) => (
                        <MemoizedSuperTrackPlayerSelect
                          key={`${superTrack.id}- ${
                            superTrack.isHidden ? "hidden" : "visible"
                          }`}
                          superTrack={superTrack}
                          jerseys={jerseys}
                          isDuplicate={calculateIsDuplicate(superTrack)}
                        />
                      ))}
                    </div>
                  </ScrollSyncPane>
                </div>
              </div>
            </Profiler>
          </ScrollSync>
        </Col>
      </Row>
      <Modal show={showColorModal} onHide={() => setShowColorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Team Colors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-flex tw-gap-4">
            <div className="mb-3">
              <div className="tw-w-32 mb-2">Left team color:</div>
              <Form.Select
                className="tw-w-36"
                value={editTeams.homeTeam.color}
                onChange={(e) =>
                  setEditTeams({
                    ...editTeams,
                    homeTeam: { ...editTeams.homeTeam, color: e.target.value },
                  })
                }
              >
                <option value="">Select color</option>
                {renderColorOptions()}
              </Form.Select>
            </div>
            <div>
              <div className="tw-w-32 mb-2">Right team color:</div>
              <Form.Select
                className="tw-w-36"
                value={editTeams.guestTeam.color}
                onChange={(e) =>
                  setEditTeams({
                    ...editTeams,
                    guestTeam: {
                      ...editTeams.guestTeam,
                      color: e.target.value,
                    },
                  })
                }
              >
                <option value="">Select color</option>
                {renderColorOptions()}
              </Form.Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColorModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleColorEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
      {!showAllTracks &&
        localSuperTracks.length > Object.keys(taskStatuses).length && (
          <Row className="justify-content-center mt-1">
            <Col xs={11} className="text-center">
              <small className="text-muted">
                Showing {Object.keys(taskStatuses).length} super tracks with
                tasks out of {localSuperTracks.length} total.
                <Button variant="link" size="sm" onClick={toggleShowAllTracks}>
                  Show All
                </Button>
              </small>
            </Col>
          </Row>
        )}
    </>
  );
});

export default SuperTrackMarkupPanel;
